import React from 'react';
import { format, isWeekend } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/PostingLog/formatters';
import { STATUS_CONFIG } from '@/utils/PostingLog/constants';
import type { PivotedLineGroup, PivotRowStatus } from '@/utils/PostingLog/pivot';

const ROW_STATUS_BADGE_CLASS: Record<PivotRowStatus, string> = {
  Posted:  STATUS_CONFIG.Posted.badgeClass,
  Failed:  STATUS_CONFIG.Failed.badgeClass,
  Partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'Not posted': STATUS_CONFIG['Not posted'].badgeClass,
};

interface PivotedLineGridProps {
  groups: PivotedLineGroup[];
  retryingIds: Set<string>;
}

export const PivotedLineGrid: React.FC<PivotedLineGridProps> = ({ groups, retryingIds }) => {
  const days = groups[0]?.cells.map((c) => c.date) ?? [];

  return (
    <div className="rounded-md border overflow-hidden">
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Project</TableHead>
              <TableHead className="whitespace-nowrap">Cost Centre</TableHead>
              {days.map((date) => (
                <TableHead
                  key={date.toISOString()}
                  className={cn('text-right whitespace-nowrap', isWeekend(date) && 'text-muted-foreground')}
                >
                  <div>{format(date, 'EEE')}</div>
                  <div className="font-normal text-xs">{format(date, 'dd/MM')}</div>
                </TableHead>
              ))}
              <TableHead className="text-right whitespace-nowrap">Total</TableHead>
              <TableHead className="text-right whitespace-nowrap">Rate</TableHead>
              <TableHead className="text-right whitespace-nowrap">Uplift</TableHead>
              <TableHead className="whitespace-nowrap">Status</TableHead>
              <TableHead className="max-w-[160px]">Error</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {groups.map((group) => {
              const isRetrying = group.failedLogIds.some((id) => retryingIds.has(String(id)));
              const allErrorMessages = group.errorMessages;
              const hasProblem = group.status === 'Failed' || group.status === 'Partial';

              return (
                <TableRow
                  key={group.key}
                  className={cn(
                    'transition-colors',
                    hasProblem && 'bg-error-50/30 shadow-[inset_2px_0_0_0_var(--color-error-500)] dark:bg-error-950/10',
                  )}
                >
                  <TableCell className="text-sm">{group.projectName}</TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{group.costCentreName}</TableCell>

                  {group.cells.map((cell) => {
                    const isEmpty = cell.logIds.length === 0;
                    const content = isEmpty ? (
                      <span className="text-muted-foreground opacity-40">—</span>
                    ) : (
                      cell.quantity.toFixed(2)
                    );

                    return (
                      <TableCell
                        key={cell.date.toISOString()}
                        className={cn(
                          'text-sm text-right whitespace-nowrap',
                          cell.hasFailed && 'bg-error-50 dark:bg-error-950/30 text-error-700 dark:text-error-400 font-medium',
                        )}
                      >
                        {cell.errorMessages.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help underline decoration-dotted">{content}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">{cell.errorMessages.join('; ')}</TooltipContent>
                          </Tooltip>
                        ) : (
                          content
                        )}
                      </TableCell>
                    );
                  })}

                  <TableCell className="text-sm text-right font-medium whitespace-nowrap">
                    {group.totalQuantity.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-sm text-right whitespace-nowrap">{formatCurrency(group.price)}</TableCell>
                  <TableCell className="text-sm text-right whitespace-nowrap">
                    {group.totalUplift !== 0
                      ? formatCurrency(group.totalUplift)
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>

                  <TableCell>
                    {isRetrying
                      ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      : <Badge className={cn('text-xs font-medium', ROW_STATUS_BADGE_CLASS[group.status])}>{group.status}</Badge>}
                  </TableCell>

                  <TableCell className="max-w-[160px]">
                    {allErrorMessages.length > 0 ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-error-700 dark:text-error-400 truncate block cursor-help">
                            {allErrorMessages.length} error{allErrorMessages.length !== 1 ? 's' : ''}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {allErrorMessages.map((msg, i) => <div key={i}>{msg}</div>)}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-muted-foreground opacity-40 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
